import Image from '../../utils/Image';
import PriceContainer from '../price/containers/PriceContainer';
import Slider from '../../utils/Slider';
import AccommodationLink, { accommodationLinkPath } from '../host/AccommodationLink';

import './HostTeaserSmall.css';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../config/redux/types';

/* eslint-disable camelcase */

interface HostTeaserSmallProps {
  id: number;
  image: string;
  images: {
    id: number;
    url: string;
  }[];
  selling_line: string;
  display_title: string;
  accommodation: {
    slug: string;
    id: string;
  };
  showPrices?: boolean;
  showImageNotSlider?: boolean;
  useLinkOutsideRouter?: boolean;
  renderPriceAlternatives: boolean;
}

const defaultProps: Pick<
  HostTeaserSmallProps,
  'showPrices' | 'showImageNotSlider' | 'useLinkOutsideRouter'
> = {
  showPrices: true,
  showImageNotSlider: false,
  useLinkOutsideRouter: false,
};

function TeaserContent({
  id,
  image,
  images,
  selling_line,
  display_title,
  accommodation,
  showPrices,
  showImageNotSlider,
  renderPriceAlternatives,
}: Omit<HostTeaserSmallProps, 'useLinkOutsideRouter'>): JSX.Element {
  function displaySlider(): boolean {
    return !showImageNotSlider && !!images;
  }

  return (
    <>
      <div
        className={`host-teaser-small-header host-teaser-small-header-wrapper-${
          displaySlider() ? 'slider' : 'image'
        }`}
      >
        <div className="host-teaser-small-header-image">
          {displaySlider() ? (
            <div className="host-teaser-small-slider">
              <Slider sliderId={`host-teaser-slider-${id}`} images={images} />
            </div>
          ) : (
            <Image className="host-teaser-small-image" src={image} />
          )}
        </div>
        <div className="host-teaser-small-header-location">{display_title}</div>
      </div>
      <div className="host-teaser-small-content">
        <h3 className="host-teaser-small-title">{selling_line}</h3>
      </div>
      {showPrices ? (
        <div className="host-teaser-small-price">
          <PriceContainer
            accommodation={{ id: parseInt(accommodation.id, 10) }}
            renderMatchingAndAlternatives={renderPriceAlternatives}
          />
        </div>
      ) : null}
    </>
  );
}

export default function HostTeaserSmall({
  accommodation,
  useLinkOutsideRouter,
  ...props
}: HostTeaserSmallProps): JSX.Element {
  const { lang } = useParams<'lang'>();
  const { arrival, durationOfStay, occupancy } = useSelector((state: RootState) => state.request);

  if (useLinkOutsideRouter) {
    return (
      <a
        href={accommodationLinkPath(lang, accommodation, arrival, durationOfStay, occupancy)}
        className="host-teaser-small"
        rel="noreferrer"
        target="_blank"
      >
        <TeaserContent accommodation={accommodation} {...props} />
      </a>
    );
  }

  return (
    <AccommodationLink accommodation={accommodation} className="host-teaser-small">
      <TeaserContent accommodation={accommodation} {...props} />
    </AccommodationLink>
  );
}

HostTeaserSmall.defaultProps = defaultProps;
